/**
 * **Cognito Hosted UI + 認可コード + PKCE**（M16。判断: `docs/m16-entry-packet.md` §3.1）。
 *
 * ## なぜこの方式か
 *
 * - **パスワードを自前の画面で扱わない**（漏洩面が最小）
 * - **認証ライブラリを足さない**（`aws-amplify` は数 MB）
 * - UserPoolClient は既に `authorizationCodeGrant` で作ってある
 * - **M16 の主題は学習体験の UI であって認証画面ではない**
 *
 * ## PKCE を省かない
 *
 * ブラウザは**クライアントシークレットを持てない**（持たせたら公開される）。
 * **PKCE が無いと、リダイレクトで漏れた認可コードを誰でもトークンに交換できる。**
 *
 * ## トークンの置き場
 *
 * - **`id` / `access` はメモリ**（タブを閉じたら消える）
 * - **`refresh` は `localStorage`**（D12 の「中断して戻れる」を成立させる）
 *
 * **リフレッシュトークンの既定有効期間は 30 日**なので、
 * **戻ってきた学習者はトークンが生きていれば Hosted UI を再び見ない**。
 * **ログイン画面を見るのは 30 日空けた人だけ**である。
 *
 * ## `localStorage` は失敗しうる
 *
 * プライベートウィンドウ・サイトデータの遮断・別ブラウザで
 * **読み書きそのものが例外を投げる**ことがある。
 * **すべて try/catch で囲み、無かった場合に正しく動く**ようにする
 * （= Hosted UI へ飛ぶだけ）。
 */

const REFRESH_KEY = 'ua.refresh_token';
const VERIFIER_KEY = 'ua.pkce_verifier';

export interface AuthConfig {
  /** Cognito のドメイン（例: `https://ua-xxx.auth.ap-northeast-1.amazoncognito.com`） */
  readonly domain: string;
  readonly clientId: string;
  /** 戻り先（`https://chat.xlabo.jp/`） */
  readonly redirectUri: string;
}

export interface Tokens {
  readonly idToken: string;
  readonly expiresAt: number;
}

/* ---------- PKCE ---------- */

const b64url = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

async function sha256(text: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return b64url(bytes.buffer);
}

/* ---------- 保存（失敗しても動く） ---------- */

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /** **黙って続ける** — 保存できないだけで、ログイン自体はできる */
  }
}

function load(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* 同上 */
  }
}

/* ---------- 認可 ---------- */

/** Hosted UI へ飛ばす（**戻ってこない**） */
export async function beginLogin(cfg: AuthConfig): Promise<void> {
  const verifier = randomVerifier();
  /**
   * **`sessionStorage` ではなく `localStorage` に置く。**
   * Hosted UI へのリダイレクトは**同じタブで起きる**ので `sessionStorage` でも
   * 動くが、**一部のブラウザはリダイレクト跨ぎで `sessionStorage` を落とす**。
   */
  store(VERIFIER_KEY, verifier);
  const challenge = b64url(await sha256(verifier));
  const url = new URL(`${cfg.domain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('redirect_uri', cfg.redirectUri);
  url.searchParams.set('scope', 'openid');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  location.assign(url.toString());
}

/**
 * リダイレクトで戻ってきた `?code=` をトークンに交換する。
 *
 * **交換したら URL から `code` を消す** — 残すと
 * **再読み込みで同じコードを使い回そうとして失敗する**
 * （認可コードは 1 度しか使えない）。
 */
export async function completeLogin(cfg: AuthConfig): Promise<Tokens | undefined> {
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  if (code === null) return undefined;

  const verifier = load(VERIFIER_KEY);
  drop(VERIFIER_KEY);
  history.replaceState({}, '', location.pathname);
  if (verifier === null) return undefined;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: verifier,
  });
  return exchange(cfg, body);
}

/**
 * **保存してある refresh でトークンを取り直す**（D12 の「中断して戻れる」）。
 * **無ければ `undefined`** — 呼び出し側が `beginLogin` へ回す。
 */
export async function resumeSession(cfg: AuthConfig): Promise<Tokens | undefined> {
  const refresh = load(REFRESH_KEY);
  if (refresh === null) return undefined;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cfg.clientId,
    refresh_token: refresh,
  });
  const t = await exchange(cfg, body);
  /**
   * **取り直せなかったら捨てる。**
   * 残すと**毎回 30 日前の死んだトークンで往復する**ことになる。
   */
  if (t === undefined) drop(REFRESH_KEY);
  return t;
}

async function exchange(cfg: AuthConfig, body: URLSearchParams): Promise<Tokens | undefined> {
  const res = await fetch(`${cfg.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) return undefined;
  const json = (await res.json()) as {
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
  if (json.id_token === undefined) return undefined;
  /** **refresh は返ってこないことがある**（refresh 交換のとき）ので、あるときだけ更新する */
  if (json.refresh_token !== undefined) store(REFRESH_KEY, json.refresh_token);
  return {
    idToken: json.id_token,
    expiresAt: Date.now() + (json.expires_in ?? 3600) * 1000,
  };
}

export function logout(): void {
  drop(REFRESH_KEY);
  drop(VERIFIER_KEY);
}

/**
 * **期限が近いか**（余裕を持って取り直す）。
 * **切れてから取り直すと、ターンの途中で 401 になる** —
 * **不可視フェーズは 78〜102 秒**（M15 実測）あるので、
 * **その間に切れない余裕**が要る。
 */
export function needsRefresh(t: Tokens, marginMs = 5 * 60 * 1000): boolean {
  return t.expiresAt - Date.now() < marginMs;
}
